/**
 * 开始页（含最近文件列表）：对外 action 入口。
 * 统一通过 `FJStartLandingAction` 访问。
 */
import type { ProjectItem } from '../../types/FJProject';
import FJConstant from '../../constants/FJConstant';
import FJLocalStore from '../../store/FJLocalStore';
import FJProjectStore from '../../store/FJProjectStore';
import type { AppThunk } from '../appThunk';
import { FJDetailsPageAction } from './FJDetailsPageAction';
import { FJModalsAction } from './FJModalsAction';
import { setGeneratingOverallProgress, setGeneratingVideoMeta } from '../slice/FJGeneratingPageSlice';
import { FJ_COMMON_VIEW, setActiveView } from '../slice/FJCommonSlice';
import {
    clickProjectThumb,
    removeProject,
    renameProject,
    setFileUrl,
    setProjects,
    clearProjectSelection,
    setSelectedProjectIds,
    toggleProjectSelected,
} from '../slice/FJStartLandingSlice';
import { hideDetailsPage, setDetailsItems } from '../slice/FJDetailsPageSlice';
import FJLongToShortStore from '@src/store/FJLongToShortStore';
import FJLongToShortUpdateStore from '@src/store/FJLongToShortUpdateStore';
import { GENERATE_STATE, PROCESS_TYPE } from '../../constants/FJEnumConstant';
import { FJFailedPageAction } from './FJFailedPageAction';
import { FJCommonAction } from './FJCommonAction';
import FJCoreConfig from '@src/core/FJCoreConfig';
import FJMessageStore, { MESSAGE_TYPE } from '@src/store/FJMessageStore';
import FJRequirementsUtil from '@src/util/FJRequirementsUtil';
import FJBrowserHistoryStore from '@src/store/FJBrowserHistoryStore';
import {
    buildLongToShortsGenerateFailEaFromTaskCallback,
    clearLtsGenerateContext,
    eaLogLongToShortsGenerateFail,
    flattenGenerateEaPayload,
    LTS_DETAILS_ENTRY,
    peekLtsGenerateContext,
} from '@src/util/FJLongToShortEa';

export type { ProjectsState, StartLandingState } from '../slice/FJStartLandingSlice';

export const FJStartLandingAction = {
    setYouTubeUrlAction:
        (url: string): AppThunk =>
        (dispatch, getState) => {
            void getState();
            dispatch(setFileUrl(url));
        },

    /** 仅请求删除接口；本地列表更新放在关弹窗后的 `applyProjectsRemovedAction`。 */
    deleteProjectsRemoteAction:
        (ids: string[]): AppThunk =>
        async (_dispatch, getState) => {
            if (ids.length === 0) {
                return;
            }
            const row = getState().startLanding.projects.items.filter(i => ids.includes(i.id));
            const eventIds = row.map(i => i.eventId).filter(Boolean);
            FJRequirementsUtil.eaLog('recent_file_delete_confirm', { eventId: eventIds.join(',') });
            const { code } = await FJLongToShortUpdateStore.deleteBlock(ids);
            if (code !== 200) {
                throw new Error('delete_block_failed');
            }
        },

    applyProjectsRemovedAction:
        (ids: string[]): AppThunk =>
        async (dispatch, getState) => {
            if (ids.length === 0) {
                return;
            }
            for (const id of ids) {
                if (FJProjectStore.getProjectId() === id) {
                    FJProjectStore.clear();
                    const { activeView } = getState().common;
                    if (
                        activeView === FJ_COMMON_VIEW.DETAILS &&
                        FJBrowserHistoryStore.isCurrentDetails() &&
                        FJBrowserHistoryStore.canNavigateBack()
                    ) {
                        FJBrowserHistoryStore.navigateBack();
                    } else {
                        dispatch(hideDetailsPage());
                        dispatch(setActiveView(FJ_COMMON_VIEW.START_LANDING));
                        FJBrowserHistoryStore.replaceLanding();
                    }
                }
                dispatch(removeProject(id));
            }
            await dispatch(FJCommonAction.fetchTaskListAction());
        },

    removeProjectsNowAction:
        (ids: string[]): AppThunk =>
        async dispatch => {
            await dispatch(FJStartLandingAction.deleteProjectsRemoteAction(ids));
            await dispatch(FJStartLandingAction.applyProjectsRemovedAction(ids));
        },

    requestMultiRemoveProjectsAction:
        (ids: string[]): AppThunk =>
        dispatch => {
            const title =
                ids.length > 1
                    ? FJLocalStore._('delete-selected-files').replace('xxx', ids.length.toString())
                    : FJLocalStore._('delete-selected-file').replace('xxx', ids.length.toString());
            dispatch(
                FJModalsAction.showWarningModalAction({
                    title: title,
                    content: FJLocalStore._('this-cannot-be-undone-the-file-will-be-removed-from-your-recent-files'),
                    closeCallback: () => {
                        dispatch(FJStartLandingAction.applyProjectsRemovedAction(ids));
                        dispatch(FJStartLandingAction.clearProjectSelectionAction());
                    },
                    footerDates: [
                        {
                            btnType: FJConstant.WARNING,
                            btnText: FJLocalStore._('delete'),
                            btnCallback: async () => {
                                await dispatch(FJStartLandingAction.deleteProjectsRemoteAction(ids));
                                dispatch(FJModalsAction.completeWarningModalAction());
                            },
                        },
                        {
                            btnType: FJConstant.CANCEL,
                            btnText: FJLocalStore._('cancel'),
                        },
                    ],
                }),
            );
        },

    setProjectSelectionAction:
        (ids: string[]): AppThunk =>
        dispatch => {
            dispatch(setSelectedProjectIds(ids));
        },

    toggleProjectSelectionAction:
        (id: string): AppThunk =>
        dispatch => {
            dispatch(toggleProjectSelected(id));
        },

    clearProjectSelectionAction: (): AppThunk => dispatch => {
        dispatch(clearProjectSelection());
    },

    removeProjectAction:
        (id: string): AppThunk =>
        dispatch => {
            dispatch(
                FJModalsAction.showWarningModalAction({
                    title: FJLocalStore._('delete-this-file'),
                    content: FJLocalStore._('this-cannot-be-undone-the-file-will-be-removed-from-your-recent-files'),
                    closeCallback: () => {
                        dispatch(FJStartLandingAction.applyProjectsRemovedAction([id]));
                    },
                    footerDates: [
                        {
                            btnType: FJConstant.WARNING,
                            btnText: FJLocalStore._('delete'),
                            btnCallback: async () => {
                                await dispatch(FJStartLandingAction.deleteProjectsRemoteAction([id]));
                                dispatch(FJModalsAction.completeWarningModalAction());
                            },
                        },
                        {
                            btnType: FJConstant.CANCEL,
                            btnText: FJLocalStore._('cancel'),
                        },
                    ],
                }),
            );
        },

    /** 项目列表行「重命名」：弹出重命名框，确认后走 `renameProjectAction`。 */
    requestRenameProjectAction:
        (id: string): AppThunk =>
        (dispatch, getState) => {
            const row = getState().startLanding.projects.items.find(i => i.id === id);
            dispatch(
                FJModalsAction.showRenameModalAction({
                    initialTitle: row?.title ?? '',
                    context: 'project',
                    targetId: id,
                }),
            );
        },

    renameProjectAction:
        (payload: { id: string; title: string }): AppThunk =>
        async (dispatch, getState) => {
            const { id, title } = payload;
            const t = title.trim();
            if (!t) {
                return;
            }
            const item = getState().startLanding.projects.items.find(i => i.id === id);
            if (item?.eventId) {
                const result = await FJLongToShortUpdateStore.renameBlock(item.eventId, t);
                if (result === undefined) {
                    return;
                }
            }
            dispatch(FJModalsAction.hideRenameModalAction());
            dispatch(renameProject({ id, title: t }));
            await dispatch(FJCommonAction.fetchTaskListAction());
        },

    tryAgainProjectAction:
        (id: string): AppThunk =>
        async (dispatch, getState) => {
            const { totalCredits, freeTimes } = getState().common;
            const item = getState().startLanding.projects.items.find(i => i.id === id);
            if (!item || item.status !== GENERATE_STATE.FAILED) {
                return;
            }
            FJRequirementsUtil.eaLog('long_to_shorts_generate_fail_try_again_click', {
                project_id: item.eventId,
            });
            const canGenerate = FJLongToShortStore.beforeCheck(totalCredits, freeTimes, item.duration);
            if (!canGenerate) {
                return;
            }

            const videoMeta = {
                thumbnailUrl: item.thumbnailUrl,
                title: item.title,
                sourceUrl: item?.videoMeta?.sourceUrl,
                resolutionLabel: item?.videoMeta?.resolutionLabel,
            };
            dispatch(setGeneratingVideoMeta(videoMeta));

            dispatch(setActiveView(FJ_COMMON_VIEW.GENERATING));
            const progressBase = FJLongToShortStore.getProcessBase(PROCESS_TYPE.BACKEND);
            const progress = item.progress ?? progressBase.start * 100;
            dispatch(setGeneratingOverallProgress(progress));
            FJLongToShortStore.setCurFailedTaskId(null);
            const eventIdForRetry = item.eventId;
            await FJLongToShortStore.regenerateTask({
                eventId: item.eventId,
                progressCallback: ({ id, progress }: { id: string; progress: number }) => {
                    if (id === item.eventId) {
                        const p = Math.min(100, progress * 100);
                        dispatch(setGeneratingOverallProgress(p));
                        dispatch(FJStartLandingAction.updataProjectProgressAction(id, p));
                    }
                },
                callback: (taskItem: ProjectItem) => {
                    if (taskItem.status === GENERATE_STATE.PENDING) {
                        dispatch(FJStartLandingAction.runPendingAction(taskItem));
                    } else {
                        eaLogLongToShortsGenerateFail(
                            buildLongToShortsGenerateFailEaFromTaskCallback(taskItem, eventIdForRetry ?? ''),
                        );
                        dispatch(
                            FJStartLandingAction.syncProjectGenerateFailedAction({
                                eventId: eventIdForRetry ?? taskItem.eventId ?? '',
                                errorCode: taskItem.errorCode,
                                taskItem,
                            }),
                        );
                        dispatch(FJFailedPageAction.showFailedPageAction());
                        FJLongToShortStore.setCurFailedTaskId(taskItem.eventId, taskItem.errorCode);
                    }
                    dispatch(FJCommonAction.fetchTaskListAction());
                    dispatch(FJCommonAction.updateTotalCreditsAction());
                },
            });
        },

    /**
     * pending（后端已完成、待人脸识别）卡片点击「继续」：进入生成中进度页 → 串行人脸 → 写回最近文件并回到首页。
     */
    resumePendingAction:
        (id: string): AppThunk =>
        async (dispatch, getState) => {
            const item = getState().startLanding.projects.items.find(i => i.id === id);
            FJRequirementsUtil.eaLog('recent_file_continue_click', { eventId: item?.eventId ?? '' });
            if (!item || item.status !== GENERATE_STATE.PENDING || !item.details?.length || !item.videoUrl) {
                return;
            }

            dispatch(setActiveView(FJ_COMMON_VIEW.GENERATING));
            const progressBase = FJLongToShortStore.getProcessBase(PROCESS_TYPE.BACKEND);
            const progress = progressBase.start * 100 + progressBase.proportion * 100;
            dispatch(setGeneratingOverallProgress(progress));
            dispatch(
                setGeneratingVideoMeta({
                    thumbnailUrl: item.thumbnailUrl,
                    title: item.title,
                    sourceUrl: item.videoMeta?.sourceUrl,
                    resolutionLabel: item.videoMeta?.resolutionLabel,
                }),
            );

            dispatch(FJStartLandingAction.runPendingAction(item));
        },
    runPendingAction:
        (item: ProjectItem): AppThunk =>
        async (dispatch, getState) => {
            await FJProjectStore.init({
                ...item,
            });
            const needExport = FJCoreConfig.needExport;
            // 阶段顺序：人脸 →（可选）导出 → 封面。人脸与导出均占解码/编码资源，跨片段保持串行；封面在导出之后避免与编码争用同一 video。
            // 各阶段内部：导出/封面的「取 blob」仍串行，多条的上传在 FJProjectStore 内并行以缩短墙钟。
            let faceFailed = false;
            if (getState().common.activeView !== FJ_COMMON_VIEW.GENERATING) {
                return;
            }
            try {
                if (!item.disableFaceData) {
                    await FJProjectStore.runFaceDetectSerial({
                        onOverallProgress: t => {
                            const base = FJLongToShortStore.getProcessBase(PROCESS_TYPE.FACE_DETECT, {
                                needExport,
                            });
                            const progress = t * base.proportion + base.start;
                            dispatch(setGeneratingOverallProgress(Math.min(100, progress * 100)));
                        },
                    });
                }
            } catch {
                faceFailed = true;
            }
            try {
                if (needExport) {
                    await FJProjectStore.runExportSelfSerial({
                        onOverallProgress: t => {
                            const base = FJLongToShortStore.getProcessBase(PROCESS_TYPE.EXPORT, {
                                needExport,
                                disableFaceData: item.disableFaceData,
                            });
                            const progress = t * base.proportion + base.start;
                            dispatch(setGeneratingOverallProgress(Math.min(100, progress * 100)));
                        },
                    });
                }
            } catch {
                /* export optional; errors already surfaced via progress/UI if needed */
            }

            const block = FJProjectStore.getSession();
            if (FJCoreConfig.isAsyncUpdate) {
                FJProjectStore.successDetails();
                FJLongToShortUpdateStore.uploadStatus(
                    item.eventId,
                    block?.details.map(d => d.id) || [],
                    GENERATE_STATE.SUCCEEDED,
                );
            } else {
                FJLongToShortUpdateStore.uploadInitialData(block);
            }

            let next = FJProjectStore.mergeRecent(getState().startLanding.projects.items);
            if (next) {
                next = next.map(f =>
                    f.id === item.id
                        ? {
                              ...f,
                              status: GENERATE_STATE.SUCCEEDED,
                              progress: 100,
                              details: f.details?.map(d => ({
                                  ...d,
                                  status: GENERATE_STATE.SUCCEEDED,
                              })),
                          }
                        : f,
                );
                dispatch(setProjects(next || []));
            }
            await dispatch(FJStartLandingAction.runCoverThumbnailsSerialAction(item));
            const details = block?.details.map(d => ({ ...d, status: GENERATE_STATE.SUCCEEDED })) || [];
            dispatch(
                FJDetailsPageAction.openDetailsAction({ ...item, details }, false, LTS_DETAILS_ENTRY.GENERATE_RESULT),
            );
            if (faceFailed) {
                FJMessageStore.addMessage(
                    FJLocalStore._('face-detection-wasnt-successful-so-the-result-may-not-look-ideal-please-try-again'),
                    MESSAGE_TYPE.ERROR,
                );
            }
            const flat = flattenGenerateEaPayload(peekLtsGenerateContext()) as Record<string, unknown>;
            FJRequirementsUtil.eaLog('long_to_shorts_generate_success', {
                ...flat,
                project_id: item.eventId ?? '',
            });
            clearLtsGenerateContext();
        },

    runCoverThumbnailsSerialAction:
        (item: ProjectItem): AppThunk =>
        async (dispatch, getState) => {
            const block = FJProjectStore.getSession();
            const eventId = FJProjectStore.getEventId();
            await FJProjectStore.runCoverThumbnailsSerial({
                onOverallProgress: t => {
                    const base = FJLongToShortStore.getProcessBase(PROCESS_TYPE.COVER_THUMBNAILS, {
                        needExport: FJCoreConfig.needExport,
                        disableFaceData: item.disableFaceData,
                    });
                    const progress = t * base.proportion + base.start;
                    dispatch(setGeneratingOverallProgress(Math.min(100, progress * 100)));
                },
            });
            if (FJProjectStore.getEventId() !== eventId) {
                return;
            }
            let next = FJProjectStore.mergeRecent(getState().startLanding.projects.items);
            if (next) {
                next = next.map(f =>
                    f.id === item.id
                        ? {
                              ...f,
                              status: GENERATE_STATE.SUCCEEDED,
                              progress: 100,
                              details: f.details?.map(d => ({
                                  ...d,
                                  status: GENERATE_STATE.SUCCEEDED,
                              })),
                          }
                        : f,
                );
                dispatch(setProjects(next || []));
            }
            const items = structuredClone(block?.details || []).map(i => ({ ...i, status: GENERATE_STATE.SUCCEEDED }));
            if (items && items.length > 0) {
                dispatch(setDetailsItems(items ?? []));
            }
        },

    clickProjectThumbAction:
        (id: string): AppThunk =>
        async (dispatch, getState) => {
            dispatch(clickProjectThumb(id));
            const item = getState().startLanding.projects.items.find(i => i.id === id);
            FJRequirementsUtil.eaLog('recent_file_card_click', { eventId: item?.eventId ?? '' });

            if (!item) {
                return;
            }
            if (item.status === GENERATE_STATE.SUCCEEDED) {
                dispatch(FJDetailsPageAction.openDetailsAction(item, true, LTS_DETAILS_ENTRY.RECENT_PROJECT));
            } else if (item.status === GENERATE_STATE.PROCESSING) {
                const videoMeta = {
                    thumbnailUrl: item.thumbnailUrl,
                    title: item.title,
                    sourceUrl: item?.videoMeta?.sourceUrl,
                    resolutionLabel: item?.videoMeta?.resolutionLabel,
                };
                dispatch(setGeneratingVideoMeta(videoMeta));
                dispatch(setActiveView(FJ_COMMON_VIEW.GENERATING));
                const progressBase = FJLongToShortStore.getProcessBase(PROCESS_TYPE.BACKEND);
                const progress = item.progress ?? progressBase.start * 100;
                dispatch(setGeneratingOverallProgress(progress));

                const processingEventId = item.eventId;
                await FJLongToShortStore.trackingTask({
                    eventId: item.eventId,
                    progressCallback: ({ id, progress }: { id: string; progress: number }) => {
                        if (id === item.eventId) {
                            const p = Math.min(100, progress * 100);
                            dispatch(setGeneratingOverallProgress(p));
                            dispatch(FJStartLandingAction.updataProjectProgressAction(id, p));
                        }
                    },
                    callback: (taskItem: ProjectItem) => {
                        if (taskItem.status === GENERATE_STATE.PENDING) {
                            dispatch(FJStartLandingAction.runPendingAction(taskItem));
                        } else {
                            eaLogLongToShortsGenerateFail(
                                buildLongToShortsGenerateFailEaFromTaskCallback(taskItem, processingEventId ?? ''),
                            );
                            dispatch(
                                FJStartLandingAction.syncProjectGenerateFailedAction({
                                    eventId: processingEventId ?? taskItem.eventId ?? '',
                                    errorCode: taskItem.errorCode,
                                    taskItem,
                                }),
                            );
                            dispatch(FJFailedPageAction.showFailedPageAction());
                            FJLongToShortStore.setCurFailedTaskId(taskItem.eventId, taskItem.errorCode);
                            dispatch(FJCommonAction.fetchTaskListAction());
                        }
                        dispatch(FJCommonAction.updateTotalCreditsAction());
                    },
                });
            }
        },

    /** 生成失败时把首页对应卡片标为 FAILED（断网时 fetchTaskList 可能拉不到最新态） */
    syncProjectGenerateFailedAction:
        (payload: { eventId: string; errorCode?: string | null; taskItem?: ProjectItem }): AppThunk =>
        (dispatch, getState) => {
            const eventId = payload.eventId?.trim();
            if (!eventId) {
                return;
            }
            const items = getState().startLanding.projects.items;
            if (!items.some(i => i.eventId === eventId)) {
                return;
            }
            const next = items.map(i => {
                if (i.eventId !== eventId) {
                    return i;
                }
                const task = payload.taskItem;
                if (task?.status === GENERATE_STATE.PENDING) {
                    return { ...i, ...task };
                }
                return {
                    ...i,
                    ...(task?.id ? task : {}),
                    status: GENERATE_STATE.FAILED,
                    errorCode: payload.errorCode ?? task?.errorCode ?? i.errorCode,
                };
            });
            dispatch(setProjects(next));
        },

    updataProjectProgressAction:
        (eventId: string, progress: number): AppThunk =>
        (dispatch, getState) => {
            if (!eventId) {
                return;
            }
            const item = getState().startLanding.projects.items.find(i => i.eventId === eventId);
            if (!item) {
                return;
            }
            const next = getState().startLanding.projects.items.map(i =>
                i.eventId === eventId ? { ...i, progress: progress } : i,
            );
            dispatch(setProjects(next || []));
        },
} as const;
